import {jsonParseSecure} from "@/utils/helpers";
import {buildGenogramBase64InputFromItem} from "@/tools/GenogramTool/lib/GenogramInputConverter";
import {revertHandwritingSpaceFromGenogramItem} from "@/tools/GenogramTool/lib/GenogramHandwritingSpaceHelper";

class ContentInputProcessor {

	async process(rawInput) {
		let result = this.createEmptyResult()

		if (this.isNormalizedInput(rawInput)) {
			result.input = rawInput
			return result
		}

		if (typeof rawInput === "string") {
			return this.parseStringInput(rawInput)
		}

		if (rawInput && typeof rawInput === "object") {
			return this.parseObjectInput(rawInput)
		}

		return result
	}

	buildGenogramCommand(genogramItem, options = {}) {
		let exportGenogramItem = this.prepareGenogramItemForExport(genogramItem, options)
		let payload = buildGenogramBase64InputFromItem(exportGenogramItem)
		let lines = [
			":::genogram",
		]

		if (options.title) {
			lines.push(`title: ${JSON.stringify(options.title)}`)
		}

		if (options.showHandwritingSpace) {
			lines.push("showHandwritingSpace: true")
		}
		if (options.preserveViewport) {
			lines.push("preserveViewport: true")
		}

		lines.push(`${payload.type}: ${payload.value}`)
		lines.push(":::")

		return lines.join("\n")
	}

	prepareGenogramItemForExport(genogramItem, options = {}) {
		let exportGenogramItem = JSON.parse(JSON.stringify(genogramItem || {}))
		if (options.showHandwritingSpace) {
			revertHandwritingSpaceFromGenogramItem(exportGenogramItem)
		}
		return exportGenogramItem
	}

	createEmptyResult() {
		return {
			input: null,
			meta: {
				title: "",
				showHandwritingSpace: false,
				preserveViewport: false,
			},
		}
	}

	isNormalizedInput(rawInput) {
		let allowedTypes = ["genogramJson", "genogramJsonBase64", "instructions", "commands"]
		return allowedTypes.includes(rawInput?.type)
	}

	parseObjectInput(rawInput) {
		let result = this.createEmptyResult()
		result.meta = this.parseMeta(rawInput)

		if (rawInput.genogramJson || rawInput.genogramItem) {
			result.input = {
				type: "genogramJson",
				value: rawInput.genogramJson || rawInput.genogramItem,
			}
			return result
		}

		if (typeof rawInput.genogramJsonBase64 === "string" && rawInput.genogramJsonBase64) {
			result.input = {
				type: "genogramJsonBase64",
				value: rawInput.genogramJsonBase64,
			}
			return result
		}

		let instructions = rawInput.instructions || rawInput.commands
		if (Array.isArray(instructions)) {
			result.input = {
				type: "instructions",
				value: instructions,
			}
		}

		return result
	}

	parseStringInput(rawInput) {
		let result = this.createEmptyResult()
		let trimmedInput = rawInput.trim()

		let jsonResult = this.parseJsonStringInput(trimmedInput)
		if (jsonResult.input) {
			return jsonResult
		}

		if (!trimmedInput.startsWith(":::genogram")) {
			return result
		}

		let blockContent = trimmedInput
			.replace(/^:::genogram\s*/, "")
			.replace(/\n:::\s*$/, "")
			.trim()
		let fields = this.parseDirectiveFields(blockContent)

		result.meta = this.parseMeta(fields)

		if (typeof fields.genogramJsonBase64 === "string" && fields.genogramJsonBase64) {
			result.input = {
				type: "genogramJsonBase64",
				value: fields.genogramJsonBase64,
			}
			return result
		}

		if (fields.genogramJson) {
			result.input = {
				type: "genogramJson",
				value: fields.genogramJson,
			}
			return result
		}

		let instructions = fields.instructions || fields.commands
		if (Array.isArray(instructions)) {
			result.input = {
				type: "instructions",
				value: instructions,
			}
		}

		return result
	}

	parseJsonStringInput(rawInput) {
		let result = this.createEmptyResult()
		let parsedValue = jsonParseSecure(rawInput, null)

		if (Array.isArray(parsedValue)) {
			result.input = {
				type: "instructions",
				value: parsedValue,
			}
			return result
		}

		if (parsedValue && typeof parsedValue === "object") {
			return this.parseObjectInput(parsedValue)
		}

		return result
	}

	parseDirectiveFields(blockContent) {
		let fields = {}
		let lines = blockContent.split("\n").map((line) => line.trim()).filter(Boolean)

		lines.forEach((line) => {
			let separatorIndex = line.indexOf(":")
			if (separatorIndex < 0) {
				return
			}

			let key = line.slice(0, separatorIndex).trim()
			let rawValue = line.slice(separatorIndex + 1).trim()
			fields[key] = this.parseFieldValue(rawValue)
		})

		return fields
	}

	parseFieldValue(rawValue) {
		if (!rawValue) {
			return ""
		}

		if (rawValue === "true") {
			return true
		}

		if (rawValue === "false") {
			return false
		}

		if (rawValue.startsWith("\"") || rawValue.startsWith("{") || rawValue.startsWith("[")) {
			let parsedValue = jsonParseSecure(rawValue, null)
			if (parsedValue !== null) {
				return parsedValue
			}
		}

		return rawValue
	}

	parseMeta(rawInput = {}) {
		return {
			title: rawInput.title || "",
			showHandwritingSpace: rawInput.showHandwritingSpace === true || rawInput.printableTemplate === true,
			preserveViewport: rawInput.preserveViewport === true,
		}
	}
}

const contentInputProcessor = new ContentInputProcessor()

export default contentInputProcessor
export {
	ContentInputProcessor,
}
